'use client';

/**
 * CutSynthesisCard — the killer Lean metric.
 *
 * Combines three signals a cutter actually cares about:
 *   1. Average daily calorie deficit (last 7 days vs goal)
 *   2. Body-weight pace (lbs lost per week, from smart-scale readings)
 *   3. Protein per lb of bodyweight (muscle-retention floor)
 *
 * Synthesizes them into a one-line verdict the user can act on:
 *   "Cutting 650 kcal/day · −0.9 lb/wk · ON PACE"
 *
 * Pace zones (sustainable lean-cut thresholds):
 *   gaining        weight up → "Not in a deficit"
 *   too-slow       <0.3 lb/wk loss → deficit not real (or eaten back)
 *   on-pace        0.3–1.2 lb/wk loss → the sweet spot
 *   too-fast       >1.2 lb/wk loss → muscle-loss risk
 *
 * Protein floor: ≥0.8 g/lb is the muscle-retention floor (Helms et al.);
 * surface a warning below it.
 *
 * Self-gates: hidden until the user has at least 3 days of food logging
 * AND a 7d weight delta in the last ~10 days.
 */

import { useEffect, useState } from 'react';
import { TrendingDown, AlertTriangle, Check } from 'lucide-react';
import { getLocalDateString } from '@/lib/db';

const KG_TO_LBS = 2.20462;

type PaceZone = 'gaining' | 'too-slow' | 'on-pace' | 'too-fast' | 'unknown';
type ProteinZone = 'adequate' | 'low' | 'very-low' | 'unknown';

interface Synthesis {
  avgDeficit: number | null;       // kcal/day (positive = under goal)
  weightDeltaLbsPerWeek: number | null;
  proteinPerLb: number | null;
  paceZone: PaceZone;
  proteinZone: ProteinZone;
  daysLogged: number;
}

function classifyPace(deltaPerWeek: number | null): PaceZone {
  if (deltaPerWeek == null) return 'unknown';
  if (deltaPerWeek > 0.3) return 'gaining';
  const loss = -deltaPerWeek;
  if (loss < 0.3) return 'too-slow';
  if (loss > 1.2) return 'too-fast';
  return 'on-pace';
}

function classifyProtein(gPerLb: number | null): ProteinZone {
  if (gPerLb == null) return 'unknown';
  if (gPerLb >= 0.8) return 'adequate';
  if (gPerLb >= 0.6) return 'low';
  return 'very-low';
}

const PACE_TONE: Record<PaceZone, { tw: string; label: string; line: string }> = {
  'gaining':   { tw: 'text-amber-400',  label: 'NOT CUTTING', line: 'Weight trending up — you\'re not in a deficit yet.' },
  'too-slow':  { tw: 'text-amber-300',  label: 'TOO SLOW',    line: 'Weight barely moving — the deficit isn\'t landing.' },
  'on-pace':   { tw: 'text-emerald-400',label: 'ON PACE',     line: 'Sustainable cut — protect muscle, hold the line.' },
  'too-fast':  { tw: 'text-red-400',    label: 'TOO FAST',    line: 'Dropping fast — risks muscle loss. Add 100-200 kcal back.' },
  'unknown':   { tw: 'text-slate-500',  label: 'LEARNING',    line: 'Log food + step on the scale for a pace read.' },
};

interface Props {
  userId?: string;
}

export default function CutSynthesisCard({ userId }: Props) {
  const [s, setS] = useState<Synthesis | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userId) return;
      try {
        const [{ getDailySummary, getNutritionGoals }, { getDailyActivityEntries }, { getBodyWeightLbs }] = await Promise.all([
          import('@/lib/nutritionService'),
          import('@/lib/db'),
          import('@/lib/weightResolver'),
        ]);

        // --- Nutrition: last 7 days ---
        const goals = await getNutritionGoals(userId).catch(() => null);
        const goalCal = goals?.calories;
        const goalProt = goals?.protein;
        let totalCal = 0;
        let totalProt = 0;
        let daysLogged = 0;
        const today = new Date(); today.setHours(0, 0, 0, 0);
        for (let i = 0; i < 7; i++) {
          if (cancelled) return;
          const d = new Date(today); d.setDate(d.getDate() - i);
          try {
            const sum = await getDailySummary(userId, getLocalDateString(d));
            if (sum && sum.totalCalories > 0) {
              totalCal += sum.totalCalories;
              totalProt += sum.totalProtein;
              daysLogged++;
            }
          } catch { /* per-day fetch is best-effort */ }
        }
        const avgCal = daysLogged > 0 ? totalCal / daysLogged : null;
        const avgProt = daysLogged > 0 ? totalProt / daysLogged : null;
        const avgDeficit = avgCal != null && typeof goalCal === 'number' && goalCal > 0
          ? Math.round(goalCal - avgCal)
          : null;

        // --- Weight: 7-day delta from scale readings ---
        const since = new Date(Date.now() - 45 * 86_400_000);
        const entries = await getDailyActivityEntries(userId, { since });
        if (cancelled) return;
        const points = entries
          .filter(e => typeof e.weight === 'number' && (e.weight as number) > 0)
          .map(e => ({ t: new Date(e.date).getTime(), lbs: (e.weight as number) * KG_TO_LBS }))
          .sort((a, b) => a.t - b.t);
        let weightDelta7d: number | null = null;
        if (points.length >= 2) {
          const latest = points[points.length - 1];
          const target = latest.t - 7 * 86_400_000;
          const tol = 4 * 86_400_000;
          let best = points[0];
          let bestDist = Math.abs(points[0].t - target);
          for (const p of points) {
            const dist = Math.abs(p.t - target);
            if (dist < bestDist && dist <= tol) { best = p; bestDist = dist; }
          }
          if (bestDist <= tol) weightDelta7d = latest.lbs - best.lbs;
        }

        // --- Protein per lb (uses dynamic weight resolver) ---
        const bodyWeightLbs = await getBodyWeightLbs(userId);
        const proteinPerLb = avgProt != null && bodyWeightLbs > 0
          ? Math.round((avgProt / bodyWeightLbs) * 100) / 100
          : null;

        if (cancelled) return;
        setS({
          avgDeficit,
          weightDeltaLbsPerWeek: weightDelta7d, // already a per-week number (7d delta)
          proteinPerLb,
          paceZone: classifyPace(weightDelta7d),
          proteinZone: classifyProtein(proteinPerLb),
          daysLogged,
        });
      } catch (e) {
        console.warn('[CutSynthesis] load failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Gate: need at least 3 days of food logging AND a real weight delta.
  if (!s) return null;
  if (s.daysLogged < 3 || s.weightDeltaLbsPerWeek == null) return null;

  const tone = PACE_TONE[s.paceZone];
  const proteinWarn = s.proteinZone === 'low' || s.proteinZone === 'very-low';

  return (
    <div className={`relative rounded-lg border ${
      s.paceZone === 'on-pace' ? 'border-emerald-500/40' :
      s.paceZone === 'too-fast' ? 'border-red-500/40' :
      s.paceZone === 'too-slow' || s.paceZone === 'gaining' ? 'border-amber-500/40' :
      'border-operator-steel/40'
    } bg-operator-slate/50 overflow-hidden`}>
      <div className="px-4 py-3">
        {/* Headline row */}
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <TrendingDown className={`w-4 h-4 ${tone.tw}`} />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] font-bold text-slate-300">
              Cut tracker
            </span>
          </div>
          <span className={`font-mono text-[10px] uppercase tracking-[0.22em] font-bold ${tone.tw}`}>
            {tone.label}
          </span>
        </div>

        {/* Stat row */}
        <div className="grid grid-cols-3 gap-2 mb-2">
          <Stat label="Deficit" value={s.avgDeficit != null ? `${s.avgDeficit > 0 ? '−' : '+'}${Math.abs(s.avgDeficit)}` : '—'} unit="kcal/d" tw={s.avgDeficit != null && s.avgDeficit > 0 ? 'text-emerald-300' : 'text-slate-400'} />
          <Stat label="Pace" value={`${s.weightDeltaLbsPerWeek! > 0 ? '+' : ''}${s.weightDeltaLbsPerWeek!.toFixed(1)}`} unit="lb/wk" tw={tone.tw} />
          <Stat label="Protein" value={s.proteinPerLb != null ? s.proteinPerLb.toFixed(2) : '—'} unit="g/lb" tw={proteinWarn ? 'text-amber-300' : 'text-slate-100'} />
        </div>

        {/* Verdict line */}
        <p className={`text-[11px] leading-snug ${tone.tw}`}>{tone.line}</p>

        {/* Protein floor warning */}
        {proteinWarn && (
          <div className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-300 leading-snug">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              Protein at {s.proteinPerLb!.toFixed(2)} g/lb — below the 0.8 g/lb floor for muscle retention in a cut. Push it up before it costs you lean mass.
            </span>
          </div>
        )}
        {s.proteinZone === 'adequate' && s.paceZone === 'on-pace' && (
          <div className="mt-2 flex items-start gap-1.5 text-[11px] text-emerald-400 leading-snug">
            <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Protein adequate, deficit landing — execute.</span>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, unit, tw }: { label: string; value: string; unit: string; tw: string }) {
  return (
    <div className="rounded-md bg-operator-void/40 border border-operator-steel/50 px-2 py-1.5">
      <div className="font-mono text-[8px] uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className={`font-heading text-base font-bold tabular-nums leading-tight mt-0.5 ${tw}`}>
        {value}
      </div>
      <div className="font-mono text-[8px] uppercase tracking-[0.16em] text-slate-600 truncate">{unit}</div>
    </div>
  );
}
